import { useState } from "react";
import { Share2, Play, ChevronDown, ChevronUp } from "lucide-react";
import VideoThumb from "@/components/VideoThumb";
import { toast } from "sonner";

export default function ObservationCard({ observation, clips, onPlayClip, index }) {
  const [expanded, setExpanded] = useState(false);

  const getClipById = (clipId) => {
    if (!clipId) return null;
    return clips?.find((c) => c.id === clipId || c.id.startsWith(clipId) || clipId.startsWith(c.id));
  };

  const handleShare = async () => {
    const text = `${observation.title}\n${observation.answer}`;
    const clipUrl = observation.supporting_clip_ids?.[0]
      ? getClipById(observation.supporting_clip_ids[0])?.s3_url
      : null;

    if (navigator.share) {
      try {
        const shareData = { title: "Aavaa Observation", text };
        if (clipUrl) shareData.url = clipUrl;
        await navigator.share(shareData);
      } catch {}
    } else {
      await navigator.clipboard.writeText(`${text}${clipUrl ? "\n" + clipUrl : ""}`);
      toast.success("Copied to clipboard");
    }
  };

  const confidenceColor = {
    high: "bg-[#8E9B82]/10 text-[#8E9B82]",
    moderate: "bg-[#D4A373]/10 text-[#D4A373]",
    low: "bg-[#E2DFD8] text-[#6B7264]",
  };

  return (
    <div
      className="bg-white rounded-[24px] border border-[#E2DFD8]/50 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] overflow-hidden animate-fade-up relative"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`observation-card-${index}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-[24px] bg-gradient-to-b from-[#8E9B82] to-[#D4A373] opacity-40" />
      <div className="p-5 pl-5">
        {/* Title + Confidence */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-outfit text-sm font-medium text-[#2F3630] flex-1 leading-snug">
            {observation.title}
          </h4>
          {observation.confidence && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-manrope font-medium flex-shrink-0 ${confidenceColor[observation.confidence] || confidenceColor.low}`}>
              {observation.confidence}
            </span>
          )}
        </div>

        {/* Answer */}
        <p className="font-manrope text-sm text-[#6B7264] leading-relaxed mb-3">
          {observation.answer}
        </p>

        {/* Evidence (collapsible) */}
        {observation.evidence && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-[#8E9B82] font-manrope mb-3 hover:text-[#7A8A6E] transition-colors"
            data-testid={`evidence-toggle-${index}`}
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Hide evidence" : "Show evidence"}
          </button>
        )}
        {expanded && observation.evidence && (
          <p className="font-manrope text-xs text-[#8E9B82]/80 italic mb-3 pl-3 border-l-2 border-[#8E9B82]/20">
            {observation.evidence}
          </p>
        )}

        {/* Supporting Clips */}
        {observation.supporting_clip_ids?.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar">
            {observation.supporting_clip_ids.slice(0, 4).map((clipId) => {
              const clip = getClipById(clipId);
              return clip ? (
                <button
                  key={clipId}
                  onClick={() => onPlayClip(clip)}
                  className="w-20 h-14 rounded-xl flex-shrink-0 border border-[#E2DFD8]/50 transition-all hover:shadow-sm overflow-hidden"
                  data-testid={`obs-clip-${clipId}`}
                >
                  <VideoThumb clipId={clip.id} className="w-full h-full rounded-xl" iconSize={12} />
                </button>
              ) : (
                <div key={clipId} className="w-20 h-14 rounded-xl bg-[#EAE6DF] flex-shrink-0 flex items-center justify-center">
                  <Play size={10} className="text-[#6B7264]/30" />
                </div>
              );
            })}
          </div>
        )}

        {/* Share */}
        <button
          data-testid={`share-obs-${index}`}
          onClick={handleShare}
          className="flex items-center gap-1.5 text-[#8E9B82] text-xs font-manrope font-medium hover:text-[#7A8A6E] transition-colors"
        >
          <Share2 size={13} strokeWidth={1.5} /> Share this observation
        </button>
      </div>
    </div>
  );
}
